import React, { LinkHTMLAttributes, FC } from 'react';
import styled from 'astroturf/react';
import {
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';

import routes from 'js/core_pre_webpack/routes';
import { currentUser } from 'js/utils/user';
import { CurrentUser } from 'js/core_post_webpack/models/user';
import { Icon } from 'js/design_system/Icon';

import PremiumLock from './PremiumLock';
import { getUnreadCount } from './api';

import 'stylesheets/design_system/navbar.scss';
import _ProfileImg from "Navbar-ProfileImg.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/nav/Navbar.tsx?styleId=ProfileImg";


/** Profile icon for account toolbar / menu item */

const ProfileImg = /*#__PURE__*/styled("img", null, {
  displayName: "ProfileImg",
  styles: _ProfileImg
});

const DEFAULT_PHOTO_URL = '/images/s65_no_pic.png';

const ProfileIcon: FC = () =>
  currentUser?.photo ? (
    <ProfileImg
      alt="Your profile photo"
      src={currentUser.photo}
      onError={({ currentTarget }) => {
        currentTarget.onerror = null;
        currentTarget.src = DEFAULT_PHOTO_URL;
      }}
    />
  ) : (
    <Icon name="person" size={32} />
  );

const NotificationBadge: FC<React.PropsWithChildren> = ({ children }) => (
  <div className="ds2-5-navbar__notification-badge">{children}</div>
);

/** Search input */
// Just a cosmetic placeholder for now - not used anywhere yet
const SearchInput = () => (
  <div className="ds2-5-navbar__search">
    <Icon name="search" size={32} className="ds2-5-navbar__search__icon" />
    <input
      type="text"
      placeholder="Search papers, people, topics…"
      className="ds2-5-navbar__search__input"
    />
  </div>
);

/** Simple link item for toolbar / menu (both internal and external) */

interface LinkBaseProps
  extends React.PropsWithChildren<LinkHTMLAttributes<HTMLAnchorElement>> {
  external?: boolean;
  // requiring these not be undefined:
  className: string;
  href: string;
}

const LinkBase: FC<LinkBaseProps> = ({
  className,
  href,
  external,
  children,
  ...props
}) => (
  <a
    className={className}
    href={href}
    target={external ? '_blank' : undefined}
    {...props}
  >
    {children}
    {external && <Icon name="arrow_outward" size={20} />}
  </a>
);

type LinkItemProps = {
  href: string;
  external?: boolean;
  title?: string; // Optional title only shown in menu, and not on toolbar
  rel?: string;
};

export const LinkItem: FC<React.PropsWithChildren<LinkItemProps>> = ({
  href,
  external,
  title,
  children,
  rel,
}) => (
  <LinkBase
    className="ds2-5-navbar__toolbar__item"
    href={href}
    external={external}
    title={title}
    rel={rel}
  >
    {children}
    <span className="ds2-5-navbar__toolbar__item__title--menu-only">
      {title}
    </span>
  </LinkBase>
);

/** Sub-menu item for toolbar / menu */

type MenuItemProps = {
  title: string;

  // If present, shown alone in toolbar, and before title in menu
  reference?: React.ReactNode;
};

export const MenuItem: FC<React.PropsWithChildren<MenuItemProps>> = ({
  title,
  reference,
  children,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const { refs, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
  });
  const click = useClick(context, {
    event: 'mousedown',
  });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  return (
    <>
      <div
        className={`ds2-5-navbar__toolbar__item ${
          isOpen ? 'ds2-5-navbar__toolbar__item--active' : ''
        }`}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {reference ? (
          <div className="ds2-5-navbar__toolbar__item__reference">
            {reference}
            <span className="ds2-5-navbar__toolbar__item__title--menu-only">
              {title}
            </span>
          </div>
        ) : (
          <>
            <span className="ds2-5-navbar__toolbar__item__title">{title}</span>
            <div className="ds2-5-navbar__toolbar__item__toolbar-icon">
              <Icon name={isOpen ? 'expand_less' : 'expand_more'} size={20} />
            </div>
          </>
        )}
        <div className="ds2-5-navbar__toolbar__item__menu-icon">
          <Icon name="navigate_next" size={24} />
        </div>
      </div>
      {isOpen && (
        <FloatingPortal>
          <FloatingOverlay
            lockScroll
            className="ds2-5-navbar__menu__overlay"
            style={{ top: 'var(--nav-height, 80px)' }}
          >
            <div
              className="ds2-5-navbar__menu--outer-container"
              ref={refs.setFloating}
              {...getFloatingProps()}
            >
              <div className="ds2-5-navbar__menu--grid-container">
                <div className="ds2-5-navbar__menu--heading">
                  <button
                    className="ds2-5-navbar__menu--heading__back-cta"
                    onClick={() => setIsOpen(false)}
                  >
                    <Icon name="arrow_left_alt" size={24} />
                  </button>
                  <div className="ds2-5-navbar__menu--heading__title">
                    {title}
                  </div>
                </div>
                <div className="ds2-5-navbar__menu--submenu-content">
                  {children}
                </div>
              </div>
            </div>
          </FloatingOverlay>
        </FloatingPortal>
      )}
    </>
  );
};

/** Menu link section to be used in body of MenuItems */

type MenuLinkSectionProps = {
  title: string;
};

export const MenuLinkSection: FC<
  React.PropsWithChildren<MenuLinkSectionProps>
> = ({ title, children }) => (
  <div className="ds2-5-navbar__menu__link-section">
    <div className="ds2-5-navbar__menu__link-section__title">{title}</div>
    <div className="ds2-5-navbar__menu__link-section__content">{children}</div>
  </div>
);

type MenuLinkProps = {
  href: string;
  external?: boolean;
  badge?: React.ReactNode;
};

export const MenuLink: FC<React.PropsWithChildren<MenuLinkProps>> = ({
  children,
  href,
  external,
  badge,
}) => (
  <LinkBase
    className="ds2-5-navbar__menu__link"
    href={href}
    external={external}
  >
    {children}
    {badge && <div className="ds2-5-navbar__menu__link__badge">{badge}</div>}
  </LinkBase>
);

type MenuMessageSectionProps = {
  title: string;
  text: string;
  ctaText: string;
  ctaHref: string;
  ctaExternal?: boolean;
};

export const MenuMessageSection: FC<MenuMessageSectionProps> = ({
  title,
  text,
  ctaText,
  ctaHref,
  ctaExternal,
}) => (
  <div className="ds2-5-navbar__menu__message-section">
    <div className="ds2-5-navbar__menu__message-section__title">{title}</div>
    <div className="ds2-5-navbar__menu__message-section__text">{text}</div>
    <LinkBase
      className="ds2-5-navbar__menu__message-section__cta"
      href={ctaHref}
      external={ctaExternal}
    >
      <span className="ds2-5-navbar__menu__message-section__cta__text">
        {ctaText}
      </span>
      {!ctaExternal && <Icon name="arrow_forward" size={18} />}
    </LinkBase>
  </div>
);

/** Hamburger menu containing nav items for small / large breakpoints */

const HamburgerMenu: FC<React.PropsWithChildren> = ({ children }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const { refs, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
  });
  const click = useClick(context, {
    event: 'mousedown',
  });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  return (
    <>
      <div
        className="ds2-5-navbar__toolbar--menu__button"
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <Icon name="menu" size={24} />
      </div>
      {isOpen && (
        <FloatingPortal>
          <FloatingOverlay
            lockScroll
            className="ds2-5-navbar__menu__overlay ds2-5-navbar__menu__overlay--small-only"
            style={{ top: 'var(--nav-height, 64px)' }}
          >
            <div
              className="ds2-5-navbar__menu--outer-container"
              ref={refs.setFloating}
              {...getFloatingProps()}
            >
              <div className="ds2-5-navbar__menu--grid-container">
                {children}
              </div>
            </div>
          </FloatingOverlay>
        </FloatingPortal>
      )}
    </>
  );
};

/** Account menu item that always displays in right of toolbar / top of menu */

const ACCOUNT_ROUTE_OPTS = {
  from_navbar: true,
  trigger: 'navbar-account-menu',
};

type LoggedInAccountMenuProps = {
  user: CurrentUser;
  messagesCount: number | null;
  unreadMentionsCount: number | null;
};

const LoggedInAccountMenu: FC<LoggedInAccountMenuProps> = ({
  user,
  messagesCount,
  unreadMentionsCount,
}) => {
  const [notificationCount, setNotificationCount] = React.useState(0);

  React.useEffect(() => {
    getUnreadCount().then(({ count }) => {
      setNotificationCount(count);
    });
  }, []);

  return (
    <MenuItem title="Profile" reference={<ProfileIcon />}>
      <MenuLinkSection title="Account">
        <MenuLink href={user.url}>Profile</MenuLink>
        <MenuLink
          href={routes.threads_url(ACCOUNT_ROUTE_OPTS)}
          badge={
            messagesCount && messagesCount > 0 ? (
              <NotificationBadge>{messagesCount}</NotificationBadge>
            ) : null
          }
        >
          Messages
        </MenuLink>
        <MenuLink
          href={routes.notifications_url(ACCOUNT_ROUTE_OPTS)}
          badge={
            notificationCount > 0 ? (
              <NotificationBadge>{notificationCount}</NotificationBadge>
            ) : null
          }
        >
          Notifications
        </MenuLink>
        <MenuLink href={routes.reading_history_url(ACCOUNT_ROUTE_OPTS)}>
          Reading history
        </MenuLink>
        <MenuLink href={routes.friend_finder_index_url(ACCOUNT_ROUTE_OPTS)}>
          Find colleagues
        </MenuLink>
        <MenuLink href="https://support.academia.edu/">Help center</MenuLink>
        <MenuLink href={routes.logout_url(ACCOUNT_ROUTE_OPTS)}>
          Log out
        </MenuLink>
      </MenuLinkSection>
      <MenuLinkSection title="Analytics">
        <MenuLink
          href={routes.mentions_url(ACCOUNT_ROUTE_OPTS)}
          badge={
            user.premium ? (
              unreadMentionsCount && unreadMentionsCount > 0 ? (
                <NotificationBadge>{unreadMentionsCount}</NotificationBadge>
              ) : null
            ) : (
              <PremiumLock />
            )
          }
        >
          Mentions
        </MenuLink>
        <MenuLink
          href={routes.analytics_dashboard_url({
            ...ACCOUNT_ROUTE_OPTS,
            page_name: user.page_name,
            subdomain: user.domain_name,
          })}
        >
          Basic analytics
        </MenuLink>
        <MenuLink
          href={
            user.premium
              ? routes.analytics_dashboard_url({
                  ...ACCOUNT_ROUTE_OPTS,
                  page_name: user.page_name,
                  subdomain: user.domain_name,
                })
              : routes.upgrade_url({
                  ...ACCOUNT_ROUTE_OPTS,
                  feature: 'analytics',
                })
          }
          badge={<PremiumLock />}
        >
          Advanced analytics
        </MenuLink>
        <MenuLink
          href={
            user.premium
              ? routes.analytics_dashboard_url({
                  ...ACCOUNT_ROUTE_OPTS,
                  page_name: user.page_name,
                  subdomain: user.domain_name,
                }) + '/activity/profile-visitors' // Not ideal, but this is how it is done elsewhere.
              : routes.upgrade_url({
                  ...ACCOUNT_ROUTE_OPTS,
                  feature: 'PROFILE_VISITORS',
                })
          }
          badge={<PremiumLock />}
        >
          Profile visitors
        </MenuLink>
        <MenuLink
          href={routes.readers_url(ACCOUNT_ROUTE_OPTS)}
          badge={<PremiumLock />}
        >
          Readers
        </MenuLink>
      </MenuLinkSection>
      <MenuLinkSection title="Academia Journals">
        <MenuLink href={routes.journals_submissions_url(ACCOUNT_ROUTE_OPTS)}>
          My submissions
        </MenuLink>
        <MenuLink
          href={routes.journals_submissions_url({
            ...ACCOUNT_ROUTE_OPTS,
            anchor: 'reviewer',
          })}
        >
          Reviewer Hub
        </MenuLink>
        <MenuLink
          href={routes.journals_submissions_url({
            ...ACCOUNT_ROUTE_OPTS,
            anchor: 'editor',
          })}
        >
          Editor Hub
        </MenuLink>
        <MenuLink
          href={routes.journals_hub_special_issue_index_url(ACCOUNT_ROUTE_OPTS)}
        >
          Special Issue Hub
        </MenuLink>
      </MenuLinkSection>
    </MenuItem>
  );
};

type AccountMenuItemProps = {
  messagesCount: number | null;
  unreadMentionsCount: number | null;
};

const AccountMenuItem: FC<AccountMenuItemProps> = ({
  messagesCount,
  unreadMentionsCount,
}) =>
  currentUser ? (
    <LoggedInAccountMenu
      user={currentUser}
      messagesCount={messagesCount}
      unreadMentionsCount={unreadMentionsCount}
    />
  ) : (
    <LinkItem
      href={routes.login_url({
        post_login_redirect_url: encodeURIComponent(window.location.href),
      })}
      title="Log in"
      rel="nofollow"
    >
      <ProfileIcon />
    </LinkItem>
  );

type NavbarProps = {
  logoSmall: React.ReactNode;
  logoLarge: React.ReactNode;
  search?: boolean;
  items: React.ReactNode;
  ctas?: React.ReactNode;
  messagesCount: number | null;
  unreadMentionsCount: number | null;
  inDevStyle?: boolean;
};

export const Navbar: FC<NavbarProps> = ({
  logoSmall,
  logoLarge,
  search,
  items,
  ctas,
  messagesCount,
  unreadMentionsCount,
  inDevStyle,
}) => (
  <nav className={`ds2-5-navbar ${inDevStyle ? 'in-dev-style' : ''}`}>
    <div className="ds2-5-navbar__logo--small">{logoSmall}</div>
    <div className="ds2-5-navbar__logo--large">{logoLarge}</div>
    {search && <SearchInput />}

    {/* Shows in large breakpoint */}
    <div className="ds2-5-navbar__toolbar">
      <div className="ds2-5-navbar__toolbar__item-group">{items}</div>
      {ctas ? (
        <div className="ds2-5-navbar__toolbar__item-group">{ctas}</div>
      ) : null}
      <AccountMenuItem
        messagesCount={messagesCount}
        unreadMentionsCount={unreadMentionsCount}
      />
    </div>

    {/* Shows in small and medium breakpoints */}
    <div className="ds2-5-navbar__toolbar--menu">
      <HamburgerMenu>
        <div className="ds2-5-navbar__menu--content">
          <AccountMenuItem
            messagesCount={messagesCount}
            unreadMentionsCount={unreadMentionsCount}
          />
          {items}
        </div>
        <div className="ds2-5-navbar__menu--footer">
          {ctas ? <div className="ds2-5-navbar__menu__ctas">{ctas}</div> : null}
        </div>
      </HamburgerMenu>
    </div>
  </nav>
);
